/**
 * The option list for a question ADK has paused the turn on.
 *
 * The sibling of `ToolConfirmationPrompt`, and the same idea one step on: a
 * confirmation turns "may I?" into a button, this turns "which one?" into a
 * list. Before it, Bird disambiguated by enumerating candidates as links and
 * waiting for a typed reply, which is what the researcher then had to phrase
 * unambiguously — for four notes all called "Methods", they could not.
 *
 * Deliberately not a modal, for the reason the confirmation prompt gives: the
 * prose above it is the context the researcher needs in order to choose.
 *
 * WHAT IS RENDERED IS WORKSPACE CONTENT. `message`, `label` and `detail` are
 * entity titles the researcher wrote. `value` is the model's opaque const and
 * is NEVER rendered — it is an entity id, and showing it would be both
 * meaningless and a small leak of internal structure into the transcript.
 */

import { Box, Button, Text, VStack } from '@chakra-ui/react';
import { useEffect } from 'react';

import { ANALYTICS_EVENTS } from '../../../constants/analytics';
import type { PendingToolChoice } from '../../../services/agentChat.service';
import { safeAnalytics } from '../../../utils/analytics';
import { MaterialIcon } from '../../ui/MaterialIcon';

interface ToolChoicePromptProps {
  readonly choice: PendingToolChoice;
  readonly onRespond: (value: string) => void;
  /** True while an answer is in flight — every option locks to prevent a double send. */
  readonly isResponding?: boolean;
}

export function ToolChoicePrompt({
  choice,
  onRespond,
  isResponding = false,
}: ToolChoicePromptProps) {
  // Fired on RENDER, which is the only moment that distinguishes "the
  // researcher was shown a control" from "the agent asked a question nobody
  // could answer" — the state #1619 was reported through for confirmations.
  //
  // Carries the option COUNT and never the options themselves: labels and
  // details are researcher-authored entity titles, so they stay out of
  // analytics entirely (rules/analytics.md: IDs only, and UGC never without an
  // egress decision). There is no id worth sending either — the const is an
  // entity id, which is exactly the thing not to put in a property bag.
  //
  // `replayed` keeps the event interpretable now that a question survives a
  // reload: an unanswered one is re-displayed every time the panel reopens, so
  // without it displayed→answered becomes a measure of how often researchers
  // reopen Bird.
  const { id: choiceId, options, replayed } = choice;
  const optionCount = options.length;
  useEffect(() => {
    safeAnalytics.track(ANALYTICS_EVENTS.CHAT_TOOL_CHOICE_DISPLAYED, {
      option_count: optionCount,
      replayed: replayed === true,
    });
  }, [choiceId, optionCount, replayed]);

  // The model authors the response schema and may write one that is not a list
  // of choices. Rendering an empty control would be a dead end, so the question
  // stands as prose and the researcher answers in the composer — which is what
  // they did before this channel existed, and what the server's own fallback
  // note describes.
  if (optionCount === 0) {
    return (
      <Box
        mt={3}
        p={3}
        borderWidth="1px"
        borderColor="border.default"
        borderRadius="md"
        bg="bg.subtle"
      >
        <Text fontSize="sm" color="text.primary">
          {choice.message}
        </Text>
      </Box>
    );
  }

  return (
    <Box
      mt={3}
      p={3}
      borderWidth="1px"
      borderColor="border.default"
      borderRadius="md"
      bg="bg.subtle"
      // Same reasoning as the confirmation prompt: `role="group"` announces
      // nothing mid-stream, and without a live region a screen-reader user is
      // left waiting on a stream that has silently stopped for input.
      role="group"
      aria-live="polite"
      aria-label={choice.message}
    >
      <VStack align="stretch" spacing={2}>
        <Text fontSize="sm" fontWeight="semibold" color="text.primary">
          {choice.message}
        </Text>
        <VStack align="stretch" spacing={1}>
          {options.map((option) => (
            <Button
              key={option.value}
              size="sm"
              variant="outline"
              justifyContent="flex-start"
              textAlign="left"
              height="auto"
              py={2}
              whiteSpace="normal"
              onClick={() => onRespond(option.value)}
              isDisabled={isResponding}
              leftIcon={<MaterialIcon name="radio_button_unchecked" boxSize={4} aria-hidden />}
            >
              <VStack align="start" spacing={0} minW={0}>
                <Text fontSize="sm" color="text.primary" fontWeight="medium">
                  {option.label}
                </Text>
                {option.detail ? (
                  // The only thing telling two same-titled options apart, which
                  // is the whole case this control exists for. Never hide it.
                  <Text fontSize="xs" color="text.muted">
                    {option.detail}
                  </Text>
                ) : null}
              </VStack>
            </Button>
          ))}
        </VStack>
      </VStack>
    </Box>
  );
}
